/**
 * Panneau d'un refuge, camping ou hébergement de l'organisation touché sur la carte :
 * son nom, quelques infos et les liens pour modifier sa fiche ou voir la fiche publique.
 * Sert aussi à créer un refuge ou un camping au point cliqué (nom, puis fiche complète).
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { OrganizationLocation } from '@web/features/organizations/types'
import { StatusPill, TypeIcon } from '../badges'
import { formatDate, formatNumber } from '../../utils/format'
import { publicLocationUrl } from '../../utils/links'
import { EDITABLE_TYPES, editLocationPath } from '../../utils/routes'

type ShowProps = { orgId: number; place: OrganizationLocation; onBack: () => void }

export function PlacePanel({ orgId, place, onBack }: ShowProps) {
  const { t } = useTranslation('gestion')
  const editable = (EDITABLE_TYPES as readonly string[]).includes(place.type)
  return (
    <div className="g-editor__details">
      <button type="button" className="g-back" onClick={onBack}>
        <span className="mdi mdi-arrow-left" aria-hidden /> {t('carte.retour_liste')}
      </button>
      <div className="g-place__head">
        <TypeIcon type={place.type} />
        <div className="g-place__title">
          <h2 className="g-editor__section">{place.name}</h2>
          <span className="g-muted">
            {t(`types.${place.type}`, { defaultValue: place.type })} · {t('lieux.modifie_le', { date: formatDate(place.updated_at) })}
          </span>
        </div>
        {place.status !== 'active' && <StatusPill status={place.status} />}
      </div>
      <dl className="g-place__stats">
        <div title={t('stats.jaime')}>
          <dt className="mdi mdi-heart-outline" aria-label={t('stats.jaime')} />
          <dd>{formatNumber(place.like_count)}</dd>
        </div>
        <div title={t('stats.a_visiter')}>
          <dt className="mdi mdi-bookmark-outline" aria-label={t('stats.a_visiter')} />
          <dd>{formatNumber(place.to_visit_count)}</dd>
        </div>
        <div title={t('stats.commentaires')}>
          <dt className="mdi mdi-comment-text-outline" aria-label={t('stats.commentaires')} />
          <dd>{formatNumber(place.comment_count)}</dd>
        </div>
        {place.rating_count ? (
          <div title={t('stats.note')}>
            <dt className="mdi mdi-star-outline" aria-label={t('stats.note')} />
            <dd>{(place.rating_avg ?? 0).toFixed(1)}</dd>
          </div>
        ) : null}
      </dl>
      <div className="g-editor__buttons">
        {editable && (
          <Link to={editLocationPath(orgId, place.type, place.id)} className="btn btn--primary">
            <span className="mdi mdi-pencil-outline" aria-hidden /> {t('carte.modifier_fiche')}
          </Link>
        )}
        <a href={publicLocationUrl(place)} target="_blank" rel="noreferrer" className="btn">
          <span className="mdi mdi-open-in-new" aria-hidden /> {t('nav.voir_fiche_publique')}
        </a>
      </div>
      <p className="g-hint">{t('carte.aide_lieu_deplacer')}</p>
    </div>
  )
}

type NewProps = {
  kind: 'refuge' | 'camping'
  busy: boolean
  onCreate: (name: string, openEditor: boolean) => void
  onCancel: () => void
}

export function NewPlacePanel({ kind, busy, onCreate, onCancel }: NewProps) {
  const { t } = useTranslation('gestion')
  const [name, setName] = useState('')
  const ok = name.trim().length > 0
  return (
    <div className="g-editor__details">
      <button type="button" className="g-back" onClick={onCancel}>
        <span className="mdi mdi-arrow-left" aria-hidden /> {t('carte.retour_liste')}
      </button>
      <h2 className="g-editor__section">{t(`lieux.nouveau_${kind}`)}</h2>
      <label className="g-field">
        <span className="g-field__label">{t('carte.nom')}</span>
        <input className="g-input" value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder={t(`carte.nom_exemple_${kind}`)} />
      </label>
      <p className="g-hint">{t('carte.aide_nouveau_lieu')}</p>
      <div className="g-editor__buttons">
        <button type="button" className="btn btn--primary" disabled={!ok || busy} onClick={() => onCreate(name.trim(), true)}>
          {t('carte.creer_completer')}
        </button>
        <button type="button" className="btn" disabled={!ok || busy} onClick={() => onCreate(name.trim(), false)}>
          {t('carte.creer')}
        </button>
      </div>
    </div>
  )
}
